import { useTranslation } from "react-i18next";
import FilterButton from "@/components/table/FilterButton";
import { Table, type ColumnDef } from "@/components/table";
import { ViewAction } from "@/components/table/actions";
import { UserIcon } from "lucide-react";
import type { TFunction } from "i18next";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useGetTeamMembersByTeamIdQuery } from "@/services/(company)/api.performance";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { PATHS } from "@/routes/paths";
import FiltersModal from "@/components/FiltersModal";
import { usePerformanceTeamMemberParams } from "../../_components/performance.params";
import PerformanceTeamMemberFilters from "../../_components/performance-manage-filter";

const buildColumns = (
  translate: TFunction,
): ColumnDef<Record<string, unknown>>[] => [
  {
    key: "memberName",
    label: translate("company:performance.col_member_name"),
    className: "w-[60%]",
  },
  {
    key: "roleInProject",
    label: translate("company:performance.col_role_in_project"),
  },
  { key: "actions", label: translate("company:performance.col_actions") },
];

interface IProps {
  /** URL-backed toolbar state, owned by the page so it can paginate too. */
  params: ReturnType<typeof usePerformanceTeamMemberParams>;
  onTotalPages: (n: number) => void;
}

export const TeamMembersList = ({ params: t, onTotalPages }: IProps) => {
  const { t: translate } = useTranslation(["company", "common"]);
  const columns = buildColumns(translate);
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const filters = t.filters;

  const sanitizedParams = (filters: Record<string, any>) =>
    Object.fromEntries(
      Object.entries(filters).filter(([, value]) => {
        if (value === null || value === undefined || value === "") return false;
        if (Array.isArray(value) && value.length === 0) return false;
        return true;
      }),
    );

  const activeParams = useMemo(
    () => sanitizedParams({ roleId: filters.roleId }),
    [filters],
  );
  const queryParams = {
    teamId: id!,
    limit: 10,
    search: t.committedSearch,
    page: t.page,
    ...activeParams,
  };

  const { data: membersData, isFetching } = useGetTeamMembersByTeamIdQuery(
    queryParams,
    {
      skip: !id,
    },
  );

  useEffect(() => {
    onTotalPages(membersData?.meta?.totalPages ?? 0);
  }, [membersData?.meta?.totalPages]);

  const rows =
    membersData?.data?.map((member) => ({
      id: member.id,
      userId: member.userId,
      memberName: (
        <div className="flex items-center gap-2.5">
          <Avatar className="bg-primary/10 border border-white size-9">
            <AvatarImage
              src={member?.user?.profileImage ?? ""}
              alt={translate("common:a11y.profile_picture_alt")}
            />
            <AvatarFallback>
              <UserIcon size={18} className="text-white" />
            </AvatarFallback>
          </Avatar>
          <span className="text-sm text-gray-800">
            {member?.user?.fullName}
          </span>
        </div>
      ),
      roleInProject: (
        <div className="flex items-center gap-1.5">
          <span className="text-sm text-gray-700">
            {member?.role?.roleName ?? "-"}
          </span>
        </div>
      ),
      actions: (
        <div className="flex items-center gap-3">
          <ViewAction
            tooltip={translate("company:performance.view_details")}
            onClick={() =>
              navigate(
                PATHS.COMPANY.COMPANY_PERFORMANCE_PATHS.PERFORMANCE_CYCLE(
                  id!,
                  member.userId,
                ),
                {
                  state: { member },
                },
              )
            }
          />
        </div>
      ),
    })) ?? [];

  return (
    <>
      <Table
        data={rows}
        loading={isFetching}
        searchText={t.search}
        columns={columns}
        onSearchChange={t.setSearch}
        onResetFilters={t.hasActive ? t.reset : undefined}
        searchPlaceholder={translate(
          "company:performance.search_member_name_placeholder",
        )}
        filters={[
          {
            type: "custom",
            key: "filter",
            render: () => (
              <FilterButton
                filtersCount={t.activeCount}
                onClick={() => setIsFilterModalOpen(true)}
                onReset={t.reset}
              />
            ),
          },
        ]}
        onRowClick={(row) =>
          navigate(
            PATHS.COMPANY.COMPANY_PERFORMANCE_PATHS.PERFORMANCE_CYCLE(
              id!,
              row.userId,
            ),
            {
              state: {
                member: membersData?.data?.find(
                  (member) => member.id === row.id,
                ),
              },
            },
          )
        }
      />
      <FiltersModal
        isOpen={isFilterModalOpen}
        onClose={() => setIsFilterModalOpen(false)}
        initialFilters={t.filters}
        defaultFilters={t.defaults}
        onApply={(newFilters) => {
          t.applyFilters(newFilters);
          setIsFilterModalOpen(false);
        }}
        renderFilters={({ filters, onChange }) => (
          <PerformanceTeamMemberFilters filters={filters} onChange={onChange} />
        )}
      />
    </>
  );
};
